import { getAdminUserDetail, useQuery } from "wasp/client/operations";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../client/components/ui/sheet";
import { Badge } from "../../../client/components/ui/badge";
import { Skeleton } from "../../../client/components/ui/skeleton";
import useTranslation from "../../../client/hooks/useTranslation";
import { prettyPaymentPlanName, PaymentPlanId } from "../../../payment/plans";

interface Props {
  userId: string | null;
  onOpenChange: (open: boolean) => void;
}

function formatDate(value: Date | null | undefined): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString();
}

/** Nom lisible du plan, en tolerant une valeur inconnue venue de la base. */
function planLabel(plan: string | null): string | null {
  if (!plan) return null;
  const known = Object.values(PaymentPlanId) as string[];
  return known.includes(plan)
    ? prettyPaymentPlanName(plan as PaymentPlanId)
    : plan;
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="border-border/60 flex items-center justify-between gap-4 border-b py-2 last:border-b-0">
    <span className="text-muted-foreground text-xs">{label}</span>
    <span className="text-foreground truncate text-sm">{value}</span>
  </div>
);

const UserDetailSheet = ({ userId, onOpenChange }: Props) => {
  const t = useTranslation();
  // `enabled` evite de requeter tant qu'aucune ligne n'a ete cliquee.
  const { data: user, isLoading } = useQuery(
    getAdminUserDetail,
    { userId: userId ?? "" },
    { enabled: !!userId },
  );

  const stats = user
    ? [
        { label: t.adminDashboard.detailTrades, value: user.tradeCount },
        { label: t.adminDashboard.detailSessions, value: user.sessionCount },
        { label: t.adminDashboard.detailNotes, value: user.noteCount },
        {
          label: t.adminDashboard.detailConnections,
          value: user.tradovateConnectionCount + user.metaApiConnectionCount,
        },
      ]
    : [];

  return (
    <Sheet open={!!userId} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>
            {user?.email ?? user?.username ?? t.adminDashboard.detailTitle}
          </SheetTitle>
          <SheetDescription>
            {[user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
              t.adminDashboard.detailTitle}
          </SheetDescription>
        </SheetHeader>

        {isLoading || !user ? (
          <div className="mt-6 flex flex-col gap-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-6">
            <div className="flex flex-wrap gap-1.5">
              {user.isAdmin && (
                <Badge variant="outline">{t.adminDashboard.adminBadge}</Badge>
              )}
              {user.hasPaymentIssue && (
                <Badge variant="destructive">
                  {user.graceDaysLeft > 0
                    ? `${t.adminDashboard.graceBadge} (${user.graceDaysLeft}j)`
                    : t.adminDashboard.expiredBadge}
                </Badge>
              )}
            </div>

            <div>
              <Row
                label={t.adminDashboard.detailPlan}
                value={planLabel(user.subscriptionPlan) ?? "Free"}
              />
              <Row
                label={t.adminDashboard.detailStatus}
                value={user.subscriptionStatus ?? t.adminDashboard.detailNone}
              />
              <Row
                label={t.adminDashboard.detailNextBilling}
                value={
                  formatDate(user.subscriptionPeriodEnd) ??
                  t.adminDashboard.detailNone
                }
              />
              <Row
                label={t.adminDashboard.detailJoined}
                value={formatDate(user.createdAt) ?? "-"}
              />
              <Row
                label={t.adminDashboard.detailLastActive}
                value={
                  formatDate(user.lastActiveAt) ??
                  t.adminDashboard.neverActive
                }
              />
              <Row
                label={t.adminDashboard.detailOnboarding}
                value={
                  user.onboardingCompletedAt
                    ? t.adminDashboard.detailOnboardingDone
                    : t.adminDashboard.detailOnboardingPending
                }
              />
              <Row
                label={t.adminDashboard.detailAcquisition}
                value={
                  user.ambassadorRef
                    ? `ref:${user.ambassadorRef}`
                    : (user.utmSource ?? t.adminDashboard.sourceDirect)
                }
              />
            </div>

            <div>
              <h3 className="text-foreground mb-2 text-sm font-semibold">
                {t.adminDashboard.detailActivity}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {stats.map(({ label, value }) => (
                  <div key={label} className="bg-muted/50 rounded-lg p-3">
                    <p className="text-foreground font-mono text-lg font-bold">
                      {value}
                    </p>
                    <p className="text-muted-foreground text-xs">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-foreground mb-2 text-sm font-semibold">
                {t.adminDashboard.detailAccounts}
                <span className="text-muted-foreground ml-2 font-mono text-xs">
                  {user.accounts.length}
                </span>
              </h3>
              {user.accounts.length === 0 ? (
                <p className="text-muted-foreground text-xs">
                  {t.adminDashboard.detailNoAccounts}
                </p>
              ) : (
                <div className="flex flex-col">
                  {user.accounts.map((account) => (
                    <div
                      key={account.id}
                      className="border-border/60 flex items-center justify-between gap-2 border-b py-2 last:border-b-0"
                    >
                      <span className="text-foreground truncate text-sm">
                        {account.name}
                      </span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px]">
                          {account.status}
                        </Badge>
                        {!account.isActive && (
                          <span
                            className="bg-muted-foreground/50 size-2 rounded-full"
                            title={t.adminDashboard.inactiveBadge}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default UserDetailSheet;
